/**
 * PlannerHistory Page — AI 行程规划历史
 *
 * 功能：查看历史生成的行程、继续编辑、重新生成
 */

import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Clock, Sparkles, MapPin, Calendar, RefreshCw, ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTrips } from '@/hooks/useTrips';

const PlannerHistory = () => {
  const { user } = useAuth();
  const { data: trips, isLoading } = useTrips(user?.id);

  if (!user) {
    return (
      <div className="min-h-screen bg-bg-primary">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h1 className="text-2xl font-bold text-text-primary mb-2">登录查看行程历史</h1>
          <p className="text-text-secondary mb-6">你的每一次 AI 规划都会被保存</p>
          <Button onClick={() => window.location.hash = '/login'} className="bg-primary text-white">
            立即登录
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary">AI 行程历史</h1>
            <p className="text-text-secondary mt-1">查看和管理你的 AI 规划记录</p>
          </div>
          <Button onClick={() => window.location.hash = '/planner'} className="bg-primary text-white">
            <Sparkles className="w-4 h-4 mr-1" /> 新建行程
          </Button>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2 text-gray-500">加载中...</span>
          </div>
        )}

        {/* Trips List */}
        {!isLoading && trips && trips.length > 0 ? (
          <div className="space-y-4">
            {trips.map((trip) => (
              <div
                key={trip.id}
                className="p-6 rounded-xl bg-white shadow-sm border border-gray-50 hover:shadow-md hover:border-primary/20 transition-all cursor-pointer group"
                onClick={() => window.location.hash = `/trip/${trip.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-text-primary group-hover:text-primary transition-colors">
                      {trip.title}
                    </h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {trip.destination}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {trip.days} 天
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(trip.created_at || trip.createdAt || Date.now()).toLocaleDateString()}
                      </span>
                    </div>
                    {trip.budget && (
                      <div className="mt-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                          预算 ¥{trip.budget}
                        </span>
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-primary transition-colors" />
                </div>
              </div>
            ))}
          </div>
        ) : !isLoading ? (
          <div className="text-center py-16">
            <Clock className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <h2 className="text-xl font-medium text-text-primary mb-2">还没有行程记录</h2>
            <p className="text-gray-400 mb-6">开启你的第一次 AI 旅行规划</p>
            <Button onClick={() => window.location.hash = '/planner'} className="bg-primary text-white">
              <Sparkles className="w-4 h-4 mr-1" /> 开始规划
            </Button>
          </div>
        ) : null}

        {/* Tips */}
        <div className="mt-10 p-6 rounded-2xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/10">
          <h3 className="font-medium text-text-primary mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" /> AI 规划提示
          </h3>
          <ul className="text-sm text-text-secondary space-y-1">
            <li>• 每次规划结果会自动保存到行程历史</li>
            <li>• 可以基于历史行程继续优化和调整</li>
            <li>• AI 会参考你的历史偏好生成更精准的行程</li>
            <li>• 相同目的地的规划会命中缓存，秒级响应</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PlannerHistory;
